import React, { useCallback, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Popup from 'components/popup/Popup'
import { ReportImageUpscale } from 'pages/ReportImagePopup/ReportImageUpscale/ReportImageUpscale'
import { IBuffetGeneralReportListDto } from 'core/api/dto/BuffetGeneralReportDto'
import {
  getReportImagePopupData,
  IReportImagePopupDto,
} from './GeneralReportImagePopupDto'
import { useTranslation } from 'react-i18next'
import { useGetPlanMenuControlOrderLineQuery } from 'core/api/PlanMenuControlApi'
import { IPlanMenuControlOrderLineDto } from 'core/api/dto/PlanMenuControlDto'
import moment from 'moment'
interface IReportImagePopupProps {
  data: IBuffetGeneralReportListDto
}

export const GeneralReportImagePopup: React.FC<IReportImagePopupProps> = (
  props,
) => {
  const { data } = props

  const [searchParams, setSearchParams] = useSearchParams()

  const id = useMemo<string | null>(() => {
    const param = searchParams.get('report-image-id')
    if (!param) return null
    return param
  }, [searchParams])

  const reportImagePopupData = useMemo(
    () => getReportImagePopupData(data),
    [data],
  )

  const itemIndex = useMemo(
    () => reportImagePopupData.findIndex((i) => i.id === id),
    [id, reportImagePopupData],
  )

  const item = useMemo<IReportImagePopupDto | undefined>(
    () => reportImagePopupData[itemIndex],
    [itemIndex, reportImagePopupData],
  )
  const prevItem = useMemo(() => {
    if (itemIndex <= 0) return

    return reportImagePopupData[itemIndex - 1]
  }, [itemIndex, reportImagePopupData])

  const nextItem = useMemo(() => {
    if (itemIndex >= reportImagePopupData.length) return

    return reportImagePopupData[itemIndex + 1]
  }, [itemIndex, reportImagePopupData])

  const onButtonPreviousClick = useCallback(() => {
    if (!prevItem) return
    searchParams.set('report-image-id', prevItem.id.toString())
    setSearchParams(searchParams.toString())
  }, [prevItem, searchParams, setSearchParams])

  const onButtonNextClick = useCallback(() => {
    if (!nextItem) return
    searchParams.set('report-image-id', nextItem.id.toString())
    setSearchParams(searchParams.toString())
  }, [nextItem, searchParams, setSearchParams])

  const closeImagePopupHandler = () => {
    setSearchParams((sp) => {
      sp.delete('report-image-id')
      return sp
    })
  }

  useEffect(() => {
    if (
      data.results.length < data.count &&
      itemIndex + 2 >= reportImagePopupData.length
    ) {
      setSearchParams((sp) => {
        const spLimit = sp.get('limit') || 0
        sp.set('limit', `${+spLimit + 1}`)
        return sp.toString()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIndex, data.count, reportImagePopupData.length, data.results.length])

  useEffect(() => {
    if (!item) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && prevItem) {
        event.preventDefault()
        onButtonPreviousClick()
      } else if (event.key === 'ArrowRight' && nextItem) {
        event.preventDefault()
        onButtonNextClick()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [item, prevItem, nextItem, onButtonPreviousClick, onButtonNextClick])

  const { t } = useTranslation()
  //Эти данные нужны, чтобы получить корректный dishId, просто id от dish не подходит, нужно именно id строки заказа
  const { data: orderData } = useGetPlanMenuControlOrderLineQuery(
    { id: item?.orderParam || 0, limit: 40, offset: 0 },
    {
      skip: item?.orderParam == null,
    },
  )
  const matchedOrderLine = useMemo<
    IPlanMenuControlOrderLineDto | undefined
  >(() => {
    if (!orderData || !item) return undefined
    return orderData.find((orderLine) => orderLine.dish?.name === item.name)
  }, [orderData, item])

  const dishId = matchedOrderLine?.id

  const formatDateTime = (iso: string | null | undefined): string | null => {
    if (!iso) return null

    return moment.parseZone(iso).format('DD.MM.YY HH:mm')
  }

  if (!item) return null

  return (
    <Popup closePopup={closeImagePopupHandler}>
      <div className="report-image-popup__container">
        <div className="report-image-popup__header">
          <span>{item.name}</span>
          {item?.dimensionImage && (
            <span className="date">{formatDateTime(item.createdAt)}</span>
          )}
          <button
            className="report-image-popup__image-wrapper"
            onClick={closeImagePopupHandler}
          >
            <i
              className={`an-ico an-ico-close`}
              style={{ fontSize: '12px', color: 'white' }}
            />
          </button>
        </div>

        <div className="report-image-popup__body">
          <div className="report-image-popup__photos">
            <ReportImageUpscale
              photo={item?.categoryImage}
              standardTempMax={item.standardTempMax}
              standardTempMin={item.standardTempMin}
              hasFact={!!item?.dimensionImage}
            />
            <ReportImageUpscale
              photo={item?.dimensionImage}
              isDimension
              isFact
              isCategory={item.isCategory}
              temperature={item.temperature}
              dimensionId={Number(item?.id.replace('-dimension', ''))}
              complianceWithStandards={item.complianceWithStandards}
              aiPredictions={item.aiPredictions}
              showTags
            />
          </div>
        </div>

        <div className="report-image-popup__footer">
          <button
            className="report-image-popup__button report-image-popup__button_prev"
            onClick={onButtonPreviousClick}
            disabled={!prevItem}
            title={!prevItem ? 'Нет предыдущего элемента' : undefined}
          >
            {t('Предыдущая')}
          </button>

          {!item.isCategory && (
            <Link
              className="report-image-popup__button report-image-popup__button__redirect btn"
              to={`/plan-menu-control?order=${
                item.orderParam
              }&dish=${dishId}&dimension=${
                item.id.split('-')[0]
              }&isRedirected=1`}
            >
              {window.innerWidth < 600 ? (
                <i className="an-ico an-ico-search" />
              ) : (
                <> {t('Перейти')}</>
              )}
            </Link>
          )}

          <button
            className="report-image-popup__button report-image-popup__button"
            onClick={onButtonNextClick}
            disabled={!nextItem}
            title={!nextItem ? 'Нет следующего элемента' : undefined}
          >
            {t('Следующая')}
          </button>
        </div>
      </div>
    </Popup>
  )
}
